import { motion } from "framer-motion";
import heroImage from "@/assets/landing/Hero.webp";
import DotGrid from "./ui/dotGrid";
import BrandMark from "./ui/brandMark";
import PillButton from "./ui/pillButton";

type Chip = {
  label: string;
  className: string;
  delay: number;
  frameClassName?: string;
};

/** Dashed frames + white caption boxes floating over the hero photo. */
const chips: Chip[] = [
  { label: "Pagos en línea", className: "left-[13%] top-[11%]", delay: 0.5 },
  { label: "Asistentes IA", className: "right-[11%] top-[9%]", delay: 0.7 },
  {
    label: "Gadgets",
    className: "right-[24%] top-[52%] hidden md:block",
    delay: 0.9,
  },
  {
    label: "Workflows",
    className: "right-[9%] top-[58%] hidden md:block",
    delay: 1.1,
  },
];

function HeroChip({ label, className, delay }: Chip) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute z-20 ${className}`}
    >
      <div className="nodric-float">
        <div
          className={`relative h-24 w-36 border border-dashed border-white/50 md:h-48 md:w-44`}
        >
          <BrandMark className="absolute bottom-2 right-2 h-8 w-8 bg-white" />
        </div>
        <div className="font-body bg-white px-5 py-2.5 text-center text-[13px] text-nodric-ink shadow-lg md:text-sm">
          {label}
        </div>
      </div>
    </motion.div>
  );
}

export default function Hero() {
  return (
    <section className="relative min-h-[860px] overflow-hidden bg-nodric-navy lg:min-h-screen">
      <img
        src={heroImage}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-nodric-navy/70 via-transparent to-nodric-navy/90" />
      <DotGrid color="rgba(255,255,255,0.22)" size={28} />



      {chips.map((chip) => (
        <HeroChip key={chip.label} {...chip} />
      ))}

      <div className="relative z-30 mx-auto flex min-h-[860px] max-w-[1500px] flex-col justify-end px-6 pt-32 pb-16 md:px-12 lg:min-h-screen lg:pb-24">
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="font-display max-w-[14ch] text-4xl leading-[1.1] font-bold tracking-[0.06em] text-white md:text-6xl lg:text-7xl"
        >
          Automatiza todo tu negocio.
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-10"
        >
          <PillButton>Saber más</PillButton>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="font-body mt-8 max-w-xl text-sm leading-relaxed text-white/85 md:text-[15px]"
        >
          La complejidad digital es el freno de la innovación. Nodric transforma
          la arquitectura de tu negocio en un ecosistema de nodos inteligentes
          que ejecutan, razonan y escalan sin intervención humana. Diseñamos el
          sistema nervioso de tu empresa.
        </motion.p>
      </div>
    </section>
  );
}
